/**
 * ProposalEmailDialog — extracted from `ProposalBuilder` (inside
 * `pages/Proposals.jsx`) as the first phased decomposition of that
 * 1,645-line component. Self-contained Dialog that picks a recipient
 * contact (existing, new, or manual), optionally includes a sign
 * link, and triggers the send-email handler in the parent.
 *
 * Props are a flat bag of the ProposalBuilder state slice this dialog
 * touches. It does NOT own any state itself — that lives in the
 * parent so the parent's `setForm`/`setClientList`/etc. callers
 * continue to work transparently. This keeps the extraction
 * mechanical (no logic move) and the diff trivial to review.
 *
 * Pulled out 133 lines from `Proposals.jsx::ProposalBuilder`.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, FileDown } from "lucide-react";
import { toast } from "sonner";
import { AddContactInline } from "./AddContactInline";
import { downloadPdfWithSaveAs } from "@/lib/downloadPdfWithSaveAs";
import { generateProposalPDF } from "./generateProposalPDF";
import { EmailDocMode, docFilename } from "@/components/common/EmailDocMode";

export function ProposalEmailDialog({
  open, onOpenChange,
  clientContacts,
  selectedContactId, setSelectedContactId,
  selectedContactEmail, setSelectedContactEmail,
  showAddContactInline, setShowAddContactInline,
  includeSignLink, setIncludeSignLink,
  proposal, form, clientList, setClientList,
  sites, totals,
  onSend, docMode, setDocMode,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Email Proposal</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Send a <strong>direct document link</strong> (the customer can view, download and sign online) or download the PDF now and attach it.</p>
          <EmailDocMode mode={docMode} onChange={setDocMode} filename={docFilename("Proposal", proposal?.proposal_number || proposal?.number || form?.name)} testId="proposal-email-doc-mode" />
          <div>
            <Label className="text-xs font-medium">Send To</Label>
            {clientContacts.length > 0 ? (
              <div className="space-y-2 mt-2">
                {/* Single-select dropdown — only ONE contact picked at a
                    time. "+ Add new contact" option opens the inline form.
                    #155 (Group G): contacts WITHOUT an email used to map
                    to `<SelectItem value="">` which Radix forbids and
                    crashes the dialog. We now key the Select by a stable
                    synthetic id (contact.id, falling back to a position
                    marker for legacy rows missing one) and look up the
                    email separately when the operator confirms send. */}
                <Select
                  value={
                    selectedContactId
                    && clientContacts.some(c => (c.id || `idx-${clientContacts.indexOf(c)}`) === selectedContactId)
                      ? selectedContactId
                      : ""
                  }
                  onValueChange={(v) => {
                    if (v === "__add_new__") {
                      setShowAddContactInline(true);
                      return;
                    }
                    setShowAddContactInline(false);
                    setSelectedContactId(v);
                    const picked = clientContacts.find(c => (c.id || `idx-${clientContacts.indexOf(c)}`) === v);
                    setSelectedContactEmail(picked?.email || "");
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="select-email-contact">
                    <SelectValue placeholder="Select a contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clientContacts.map((contact, idx) => {
                      const stableId = contact.id || `idx-${idx}`;
                      return (
                        <SelectItem key={stableId} value={stableId} data-testid={`option-email-contact-${stableId}`}>
                          <span className="font-medium">{contact.name || "(unnamed)"}</span>
                          {contact.email && <span className="text-xs text-slate-500"> · {contact.email}</span>}
                          {!contact.email && <span className="text-xs text-rose-500"> · no email on file</span>}
                          {contact.is_primary && <span className="text-[10px] text-blue-600 font-medium"> (Primary)</span>}
                        </SelectItem>
                      );
                    })}
                    <SelectItem value="__add_new__" data-testid="option-add-new-contact">
                      <span className="text-blue-600 font-medium">+ Add new contact…</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {showAddContactInline && (
                  <div className="mt-2">
                    <AddContactInline
                      clientId={form.client_id}
                      siteId={form.site_id}
                      onAdded={(c) => {
                        setSelectedContactEmail(c.email);
                        setShowAddContactInline(false);
                        setClientList(prev => prev.map(cl => cl.id === form.client_id
                          ? { ...cl, contacts: [...(cl.contacts || []), c] }
                          : cl));
                      }}
                    />
                  </div>
                )}
                <div className="pt-2">
                  <Label className="text-xs">Or enter email manually (won't save to customer)</Label>
                  <Input type="email" value={clientContacts.some(c => c.email === selectedContactEmail) ? "" : selectedContactEmail} onChange={e => setSelectedContactEmail(e.target.value)} placeholder="email@example.com" className="mt-1" data-testid="input-manual-email" />
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">No saved contacts for this customer. The contact below will be <strong>saved back to the customer</strong> on send.</div>
                <AddContactInline
                  clientId={form.client_id}
                  siteId={form.site_id}
                  onAdded={(c) => {
                    setSelectedContactEmail(c.email);
                    // Refresh client list so the contact picker rebuilds
                    setClientList(prev => prev.map(cl => cl.id === form.client_id
                      ? { ...cl, contacts: [...(cl.contacts || []), c] }
                      : cl));
                  }}
                />
                <Label className="text-xs">Or enter email manually (won't save back to customer)</Label>
                <Input type="email" value={selectedContactEmail} onChange={e => setSelectedContactEmail(e.target.value)} placeholder="email@example.com" data-testid="input-manual-email" />
              </div>
            )}
          </div>
          {proposal && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="include-sign-link-toggle">
              <input type="checkbox" checked={includeSignLink} onChange={e => setIncludeSignLink(e.target.checked)} className="h-4 w-4 accent-blue-600 rounded" />
              <span className="text-sm text-slate-700">Include Review &amp; Sign link (recommended)</span>
            </label>
          )}
          <div className="flex justify-between items-center gap-2">
            <Button variant="outline" size="sm"
              onClick={async () => {
                if (proposal?.id) {
                  const baseUrl = process.env.REACT_APP_BACKEND_URL;
                  const url = `${baseUrl}/api/proposals/${proposal.id}/pdf?download=1`;
                  const safeName = (proposal.name || form.name || "Proposal").replace(/[^a-z0-9]+/gi, "_");
                  try {
                    const result = await downloadPdfWithSaveAs(url, `ABS_Proposal_${safeName}.pdf`);
                    if (result === "save-as") toast.success("PDF saved");
                    else if (result === "cancelled") { /* user dismissed save dialog */ }
                    else if (result === "anchor") toast.success("PDF downloading...");
                    else toast.success("Opening PDF download...");
                  } catch (err) { toast.error("Download failed: " + (err?.message || "unknown error")); }
                  return;
                }
                try { generateProposalPDF(form, clientList, sites, totals); }
                catch (err) { toast.error("Failed to generate PDF: " + (err?.message || "unknown error")); }
              }}
              data-testid="btn-download-proposal-pdf-dialog">
              <FileDown className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={onSend} data-testid="btn-send-proposal-email" className="bg-slate-900 hover:bg-slate-800 text-white">
                <Mail className="w-4 h-4 mr-1" /> {docMode === "attach" ? "Download & Email" : "Send Proposal Link"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
